import QueryString from 'qs';
import TabsController from '../components/common/tabs/TabsController';
import store from '../store';
import Defaults from './default';

declare global {
  interface Window {
    CONTEXT: string;
    URL_REQUEST_AJAX: string;
    TAB_ID_REQUEST: string;
    TEMPLATE_NAME: string;
    TOKEN_ID: string;
    Version: string;
    TAB_ID: string;
  }
}

export function getCurrentTabsString() {
  return encodeURIComponent(
    `${TabsController.open.join(',')}~${
      document.scrollingElement?.scrollTop ?? 0
    }`,
  );
}

const {
  URL_REQUEST_AJAX,
  TAB_ID_REQUEST,
  TOKEN_ID,
  CURRENT_TAB,
  IS_EDIT_GRID_MODAL,
  specificListData,
} = window;

let { CONTEXT } = window;
if (CONTEXT && CONTEXT.endsWith('/'))
  CONTEXT += CONTEXT.slice(0, CONTEXT.length - 1);

let editionMode = '';
if (IS_EDIT_GRID_MODAL === true) editionMode = '&editionMode=true';

const PROD = {
  ENV: 'prod',
  CURRENT_TAB,
  PROCESS_FIELD_SUBMIT_URL: `${CONTEXT}/apia.execution.FormAction.run?action=processFieldSubmit&isAjax=true&react=true${editionMode}${TAB_ID_REQUEST}`,
  CHECK_WEB_DAV_DOCUMENTS_LOCKS_URL: `${CONTEXT}${URL_REQUEST_AJAX}?action=checkWebDavDocumentsLocks&isAjax=true${TAB_ID_REQUEST}`,
  HAS_SIGNABLE_FORMS_URL: () =>
    `${CONTEXT}${URL_REQUEST_AJAX}?action=hasSignableForms&isAjax=true&${
      store.getState().appStateSlice.actionState === 'btnConfirm'
        ? 'prevSteps=true&'
        : ''
    }appletToken=${window.appletToken ?? ''}${TAB_ID_REQUEST}`,
  CONFIRM_FORM_URL: () =>
    `${CONTEXT}${URL_REQUEST_AJAX}?action=confirm&asXML=true&appletToken=${
      window.appletToken ?? ''
    }${TAB_ID_REQUEST}`,
  CLEAR_EVAL_PATH: `${CONTEXT}${URL_REQUEST_AJAX}?action=clearEvalPath${TAB_ID_REQUEST}`,
  FORM_GOTO_NEXT_STEP_URL: () =>
    `${CONTEXT}${URL_REQUEST_AJAX}?action=gotoNextStep&currentTab=${getCurrentTabsString()}${TAB_ID_REQUEST}&fromPanel=${
      window.FROM_PANEL
    }`,
  FORM_GOTO_PREVIOUS_STEP_URL: () =>
    `${CONTEXT}${URL_REQUEST_AJAX}?action=gotoPrevStep&currentTab=${getCurrentTabsString()}${TAB_ID_REQUEST}&fromPanel=${
      window.FROM_PANEL
    }`,
  FORM_SUBMIT_FIELD_EVENTS_URL: () =>
    `${CONTEXT}/apia.execution.FormAction.run?action=fireFieldEvent&currentTab=${getCurrentTabsString()}${TAB_ID_REQUEST}`,
  FORM_AJAX_FIELD_EVENTS_URL: `${CONTEXT}/apia.execution.FormAction.run?action=fireFieldEvent&isAjax=true${TAB_ID_REQUEST}`,
  FORM_CHECK_IF_WIZARD_URL: `${CONTEXT}${URL_REQUEST_AJAX}?action=checkWizzard${TAB_ID_REQUEST}`,
  GRID_PROCESS_FIELD_URL: `${CONTEXT}/apia.execution.FormAction.run?action=processGridFieldAction${TAB_ID_REQUEST}`,
  GRID_EDIT_FORM_URL: `${CONTEXT}/apia.execution.FormAction.run?action=editGrid${TAB_ID_REQUEST}`,
  GET_DOC_TYPE_INFORMATION_URL: `${CONTEXT}/apia.execution.FormAction.run?action=getDocTypeInformation&isAjax=true${editionMode}${TAB_ID_REQUEST}`,
  CHECK_LOCK_DOCUMENT_URL: `${CONTEXT}/apia.execution.FormAction.run?action=checkLockDocument&isAjax=true${editionMode}${TAB_ID_REQUEST}`,
  LOCK_DOCUMENT_URL: `${CONTEXT}/apia.execution.FormAction.run?action=lockDocument&isAjax=true${editionMode}${TAB_ID_REQUEST}`,
  IS_WEBDAV_DOCUMENT_LOCKED_URL: `${CONTEXT}${URL_REQUEST_AJAX}?action=isWebDavDocumentLocked&isAjax=true${TAB_ID_REQUEST}`,
  DOWNLOAD_DOC_URL: `${CONTEXT}/page/generic/downloadAttDoc.jsp?action=download${editionMode}${TAB_ID_REQUEST}`,
  AJAX_UPLOAD_FILE_START_URL: `${CONTEXT}/apia.execution.FormAction.run?action=ajaxUploadStart&isAjax=true${editionMode}${TAB_ID_REQUEST}`,
  AJAX_UPLOAD_FILE_URL: `${CONTEXT}/apia.execution.FormAction.run?action=ajaxUploadFile&isAjax=true${editionMode}${TAB_ID_REQUEST}`,
  AJAX_REMOVE_FILE_URL: `${CONTEXT}/apia.execution.FormAction.run?action=ajaxRemoveDocument&isAjax=true${editionMode}${TAB_ID_REQUEST}`,
  AJAX_UPLOAD_FILE_STATUS_URL: `${CONTEXT}/apia.execution.FormAction.run?action=ajaxUploadFileStatus&isAjax=true${editionMode}${TAB_ID_REQUEST}`,
  SAVE_TASK_URL: () =>
    `${CONTEXT}${URL_REQUEST_AJAX}?action=saveTask&currentTab=${getCurrentTabsString()}${TAB_ID_REQUEST}`,
  RELEASE_TASK_URL: () =>
    `${CONTEXT}${URL_REQUEST_AJAX}?action=release&currentTab=${getCurrentTabsString()}${TAB_ID_REQUEST}`,
  TREE_IMG_URL: `${CONTEXT}/getImage.run?image=`, // <- review url
  CAPTCHA_IMG_URL: `${CONTEXT}/captchaImg?captchaName=`, // <- review url
  CAPTCHA_RELOAD_IMG_URL: `${CONTEXT}/captchaImg?captchaName=`, // <- review url
  CAPTCHA_AUDIO_URL: `${CONTEXT}/captchaAudio?captchaName=`, // <- review url

  // AUTH
  LOGIN_URL: `${CONTEXT}/apia.security.LoginAction.run?action=login`,
  PASSWORD_CHANGE_REQUEST_URL: `${CONTEXT}/apia.security.LoginAction.run?action=remember&tokenId=${TOKEN_ID}&react=true`,
  CHANGE_PWD_URL: `${CONTEXT}/apia.security.LoginAction.run?action=changePwd&tokenId=${TOKEN_ID}&react=true`,
  CHANGE_PWD_EXTERNAL_URL: `${CONTEXT}/apia.security.LoginAction.run?action=changePasswordFromExternal&tokenId=${TOKEN_ID}&react=true`,
  GOTO_SPLASH_URL: `${CONTEXT}/apia.security.LoginAction.run?action=gotoSplash&tabId=${window.TAB_ID}&react=true`,

  // SCHEDULE
  SCHEDULE_INITIAL_LOAD: `${CONTEXT}/apia.design.TaskSchedulerAction.run?action=getExecutionSchedulerWeek`,

  // FINDER OK
  FINDER_FIRST_STRUCTURE_LOAD: (ajaxUrl: string) =>
    `${CONTEXT}${ajaxUrl}?react=true${TAB_ID_REQUEST}`,
  FINDER_REFRESH_DATA: (ajaxUrl: string) =>
    `${CONTEXT}${ajaxUrl}?isAjax=true${TAB_ID_REQUEST}&action=refresh&timestamp=${Date.now()}`,
  FINDER_GET_FILTERS_CONDITIONS: (ajaxUrl: string) =>
    `${CONTEXT}${ajaxUrl}?action=filterOptions${TAB_ID_REQUEST}`, // OK
  FINDER_SELECT_ROW_GET_DATA: (ajaxUrl: string) =>
    `${CONTEXT}${ajaxUrl}?action=getValues${TAB_ID_REQUEST}`,
  FINDER_CHECK_MODAL_VALUE: () =>
    `${CONTEXT}/apia.execution.FormAction.run?action=checkModalValue&forGrid=false${TAB_ID_REQUEST}`,

  // Administration

  /*
    GROUPS
  */
  ADMIN_GET_PAGE: (pageNumber: number, action = 'page') =>
    `${CONTEXT}${specificListData?.ajaxUrl}?${TAB_ID_REQUEST}&isAjax=true&action=${action}&timestamp=${Date.now()}&pageNumber=${pageNumber}`,
  ADMIN_REFRESH_PAGE: () =>
    `${CONTEXT}${specificListData?.ajaxUrl}?${TAB_ID_REQUEST}&isAjax=true&action=refresh&timestamp=${Date.now()}`,
  ADMIN_MORE_INFO: () =>
    `${CONTEXT}${
      specificListData?.ajaxUrl ?? window.URL_REQUEST_AJAX
    }?${TAB_ID_REQUEST}&isAjax=true&action=adtInfo`,
  ADMIN_FILTER: (action = 'filter') =>
    `${CONTEXT}${specificListData?.ajaxUrl}?${TAB_ID_REQUEST}&isAjax=true&action=${action}&timestamp=${Date.now()}`,
  ADMIN_CREATE_EXCEL_FILE: () =>
    `${CONTEXT}${
      specificListData?.ajaxUrl ?? window.URL_REQUEST_AJAX
    }?${TAB_ID_REQUEST}&isAjax=true&action=createExcelFile`,
  ADMIN_DOWNLOAD_LIST: () =>
    `${CONTEXT}${
      specificListData?.ajaxUrl ?? window.URL_REQUEST_AJAX
    }?${TAB_ID_REQUEST}&action=download`,
  ADMIN_UPLOAD: () =>
    `${CONTEXT}${
      specificListData?.ajaxUrl ?? window.URL_REQUEST_AJAX
    }?${TAB_ID_REQUEST}&action=ajaxUploadStart&isAjax=true`,
  ADMIN_UPLOAD_FILE_STATUS: () =>
    `${CONTEXT}${
      specificListData?.ajaxUrl ?? window.URL_REQUEST_AJAX
    }?${TAB_ID_REQUEST}&action=ajaxUploadFileStatus&isAjax=true`,
  ADMIN_SORT: () =>
    `${CONTEXT}${specificListData?.ajaxUrl}?${TAB_ID_REQUEST}&action=sort&timestamp=${Date.now()}`,
  ADMIN_CREATE: () =>
    `${CONTEXT}${
      specificListData?.ajaxUrl ?? window.URL_REQUEST_AJAX
    }?${TAB_ID_REQUEST}&action=create&react=true`,
  ADMIN_UPDATE: (id: number | string) =>
    `${CONTEXT}${
      specificListData?.ajaxUrl ?? window.URL_REQUEST_AJAX
    }?${TAB_ID_REQUEST}&action=update&id=${id}&react=true`,
  ADMIN_GET_ENVIRONMENTS: () =>
    `${CONTEXT}${
      specificListData?.ajaxUrl ?? window.URL_REQUEST_AJAX
    }?${TAB_ID_REQUEST}&isAjax=true&action=getEnvironments&react=true`,

  ADMIN_GET_IMAGES: () =>
    `${CONTEXT}/apia.modals.ImagesAction.run?${TAB_ID_REQUEST}`,
  ADMIN_CONFIRM: () =>
    `${CONTEXT}${
      specificListData?.ajaxUrl ?? window.URL_REQUEST_AJAX
    }?${TAB_ID_REQUEST}&action=confirm&isAjax=true`,
  ADMIN_CANCEL: () =>
    `${CONTEXT}${
      specificListData?.ajaxUrl ?? window.URL_REQUEST_AJAX
    }?${TAB_ID_REQUEST}&action=list`,
  ADMIN_DELETE: (id: string) =>
    `${CONTEXT}${
      specificListData?.ajaxUrl ?? window.URL_REQUEST_AJAX
    }?${TAB_ID_REQUEST}&action=delete&isAjax=true&id=${id}`,

  ADMIN_CLONE: () =>
    `${CONTEXT}${
      specificListData?.ajaxUrl ?? window.URL_REQUEST_AJAX
    }?${TAB_ID_REQUEST}&action=clone&isAjax=true`,
  ADMIN_LOAD_DEPS: (id: string) =>
    `${CONTEXT}${
      specificListData?.ajaxUrl ?? window.URL_REQUEST_AJAX
    }?${TAB_ID_REQUEST}&action=loadDeps&isAjax=true&id=${id}`,
  ADMIN_DOWNLOAD_DEPS: (id: string) =>
    `${CONTEXT}${
      specificListData?.ajaxUrl ?? window.URL_REQUEST_AJAX
    }?${TAB_ID_REQUEST}&action=downloadDeps&isAjax=true&id=${id}`,

  GROUPS_DEFAULT_IMAGE: `${CONTEXT}/images/uploaded/-736769579.JPEG`,

  IMAGE_SELECTOR_DEFAULT_IMAGE:
    'http://localhost:8080/Apia/images/uploaded/-736769579.JPEG',
  /*
    TABLE MODAL
  */
  TABLE_MODAL_FILTER: (modalClass: string) =>
    `${CONTEXT}/${modalClass}?${TAB_ID_REQUEST}&isAjax=true&action=filter&timestamp=${Date.now()}`,
  TABLE_MODAL_PAGE: (modalClass: string, pageNumber: number) =>
    `${CONTEXT}/${modalClass}?${TAB_ID_REQUEST}&isAjax=true&action=page&timestamp=${Date.now()}&pageNumber=${pageNumber}`,
  TABLE_MODAL_REFRESH: (modalClass: string) =>
    `${CONTEXT}/${modalClass}?${TAB_ID_REQUEST}&action=refresh&isAjax=true&timestamp=${Date.now()}`,
  TABLE_MODAL_SORT: (modalClass: string) =>
    `${CONTEXT}/${modalClass}?action=sort&isAjax=true&${TAB_ID_REQUEST}&timestamp=${Date.now()}`,

  TOGGLE_FAVORITE: (ajaxUrl: string, functionId: number) =>
    `${CONTEXT}${ajaxUrl}?${TAB_ID_REQUEST}&action=toggleFav&favFncId=${functionId}&isAjax=true`,

  APIA_URL: (
    ajaxUrl: string,
    action: string,
    etc?: Record<string, string> | string,
  ) => {
    const queryString = QueryString.stringify(etc);
    return `${CONTEXT}${ajaxUrl}?action=${action}${TAB_ID_REQUEST}&${queryString}`;
  },
};

const config = { ...PROD, ...Defaults } as typeof PROD &
  typeof Defaults &
  Record<string, string>;
export default config;
